// manufacturing/ManufacturingDashboard.jsx
import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { setToogleHeader } from '../../core/redux/action';
import { toast } from "react-toastify";
import {
    RefreshCw, TrendingUp, Package,
    Settings, AlertTriangle, DollarSign,
    BarChart2, Calendar, ArrowRight,
    ChevronRight, ShoppingBag, Database
} from 'feather-icons-react/build/IconComponents';
import { manufacturingActions } from "../../core/action/manufacturingAction";
//import { rawMaterialAPI } from "../../core/action/api";
import ImageWithBasePath from '../../core/img/imagewithbasebath';


const ManufacturingDashboard = () => {
    const dispatch = useDispatch();
    const data = useSelector((state) => state.toggle_header);

    // State for dashboard data
    const [loading, setLoading] = useState(true);
    const [dashboardData, setDashboardData] = useState({
        summary: {
            total_raw_materials: 0,
            total_finished_goods: 0,
            total_productions: 0,
            total_waste_cost: 0,
            total_production_value: 0,
            conversion_rate: 0
        },
        stock_summary: {
            raw_materials: {
                head_office_stock: 0,
                manufacturing_stock: 0,
                total_value: 0,
                low_stock_items: 0
            },
            finished_goods: {
                manufacturing_stock: 0,
                warehouse_stock: 0,
                total_value: 0,
                low_stock_items: 0
            }
        },
        recent_activities: {
            dispatches: [],
            receives: [],
            productions: []
        },
        pending_actions: {
            pending_dispatches: 0,
            pending_receives: 0,
            pending_transfers: 0,
            productions_to_convert: 0
        },
        alerts: {
            low_stock_raw: [],
            low_stock_finished: [],
            pending_approvals: []
        },
        charts: {
            monthly_production: [],
            waste_by_type: [],
            top_raw_materials: []
        }
    });

    // State for filters
    const [filters, setFilters] = useState({
        date_range: 'month',
        from_date: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
        to_date: new Date().toISOString().split('T')[0]
    });

    // State for refresh
    const [lastUpdated, setLastUpdated] = useState(new Date());
    const [refreshing, setRefreshing] = useState(false);

    // Load all dashboard data
    const loadDashboardData = useCallback(async () => {
        setLoading(true);
        try {
            console.log('=== Loading Dashboard Data ===');

            // 1. Get manufacturing summary
            console.log('Fetching manufacturing summary...');
            const summaryResult = await manufacturingActions.getManufacturingSummary(filters);
            console.log('Summary Result:', summaryResult);

            // 2. Get raw material stock
            console.log('Fetching raw materials...');
            const rawStockResult = await manufacturingActions.getRawMaterials({ limit: 1000 });
            console.log('Raw Materials Result:', rawStockResult);

            // 3. Get finished goods stock
            console.log('Fetching finished goods stock...');
            const finishedStockResult = await manufacturingActions.getFinishedGoodsManufacturingStock();
            console.log('Finished Goods Result:', finishedStockResult);

            // 4. Get recent dispatches
            console.log('Fetching dispatches...');
            const dispatchesResult = await manufacturingActions.getDispatches({ limit: 5 });
            console.log('Dispatches Result:', dispatchesResult);

            // 5. Get recent receives
            console.log('Fetching receives...');
            const receivesResult = await manufacturingActions.getReceives({ limit: 5 });
            console.log('Receives Result:', receivesResult);

            // 6. Get recent productions
            console.log('Fetching productions...');
            const productionsResult = await manufacturingActions.getProductions({ limit: 5 });
            console.log('Productions Result:', productionsResult);

            // 7. Get unconverted productions
            console.log('Fetching unconverted productions...');
            const unconvertedResult = await manufacturingActions.getUnconvertedProductions();
            console.log('Unconverted Result:', unconvertedResult);

            // 8. Get pending transfers
            console.log('Fetching pending transfers...');
            const transfersResult = await manufacturingActions.getTransfers({ status: 'pending', limit: 10 });
            console.log('Transfers Result:', transfersResult);

            // Extract data safely with fallbacks
            const rawMaterialsData = rawStockResult?.data || [];
            const finishedGoodsData = finishedStockResult?.data || [];
            const summaryData = summaryResult?.data?.summary || {};
            const monthlyData = summaryResult?.data?.monthly || [];

            console.log('Raw Materials Data Count:', rawMaterialsData.length);
            console.log('Finished Goods Data Count:', finishedGoodsData.length);

            // Process data
            const processedData = {
                summary: {
                    total_raw_materials: rawMaterialsData.length,
                    total_finished_goods: finishedGoodsData.length,
                    total_productions: summaryData.total_productions || 0,
                    total_waste_cost: summaryData.total_wastage_cost || 0,
                    total_production_value: summaryData.total_production_cost || 0,
                    conversion_rate: calculateConversionRate(monthlyData)
                },
                stock_summary: {
                    raw_materials: calculateRawMaterialStock(rawMaterialsData),
                    finished_goods: calculateFinishedGoodsStock(finishedGoodsData)
                },
                recent_activities: {
                    dispatches: dispatchesResult?.data || [],
                    receives: receivesResult?.data || [],
                    productions: productionsResult?.data || []
                },
                pending_actions: {
                    pending_dispatches: (dispatchesResult?.data || []).filter(d => d.status === 'pending').length,
                    pending_receives: (receivesResult?.data || []).filter(r => r.status === 'pending').length,
                    pending_transfers: (transfersResult?.data || []).filter(t => t.status === 'pending').length,
                    productions_to_convert: unconvertedResult?.total || 0
                },
                alerts: {
                    low_stock_raw: rawMaterialsData.filter(m => (m.quantity || 0) <= (m.reorder_level || 100)),
                    low_stock_finished: finishedGoodsData.filter(f => (f.manufacturing_stock || 0) <= (f.alert_quantity || 500)),
                    pending_approvals: []
                },
                charts: {
                    monthly_production: monthlyData,
                    waste_by_type: calculateWasteByType(summaryData),
                    top_raw_materials: rawMaterialsData.slice(0, 5)
                }
            };

            console.log('Processed Dashboard Data:', processedData);
            setDashboardData(processedData);

        } catch (error) {
            console.error('Error loading dashboard:', error);
            toast.error('Failed to load dashboard data: ' + (error.message || 'Unknown error'));

            // Set empty data to avoid crashes
            setDashboardData({
                summary: {
                    total_raw_materials: 0,
                    total_finished_goods: 0,
                    total_productions: 0,
                    total_waste_cost: 0,
                    total_production_value: 0,
                    conversion_rate: 0
                },
                stock_summary: {
                    raw_materials: { head_office_stock: 0, manufacturing_stock: 0, total_value: 0, low_stock_items: 0 },
                    finished_goods: { manufacturing_stock: 0, warehouse_stock: 0, total_value: 0, low_stock_items: 0 }
                },
                recent_activities: { dispatches: [], receives: [], productions: [] },
                pending_actions: { pending_dispatches: 0, pending_receives: 0, pending_transfers: 0, productions_to_convert: 0 },
                alerts: { low_stock_raw: [], low_stock_finished: [], pending_approvals: [] },
                charts: { monthly_production: [], waste_by_type: { raw_material: 0, finished_good: 0 }, top_raw_materials: [] }
            });
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [filters]);

    // Calculate conversion rate
    const calculateConversionRate = (monthlyData) => {
        if (!monthlyData || monthlyData.length === 0) return 0;
        const totalProd = monthlyData.reduce((sum, m) => sum + (m.productions || 0), 0);
        const totalWaste = monthlyData.reduce((sum, m) => sum + (m.waste_cost || 0), 0);
        return totalProd > 0 ? ((totalProd - totalWaste) / totalProd * 100).toFixed(1) : 0;
    };

    // Calculate raw material stock summary
    const calculateRawMaterialStock = (materials) => {
        const head_office_stock = materials.reduce((sum, m) => sum + (parseFloat(m.quantity) || 0), 0);
        const total_value = materials.reduce((sum, m) => sum + ((parseFloat(m.quantity) || 0) * (parseFloat(m.cost_price) || 0)), 0);
        const low_stock_items = materials.filter(m => parseFloat(m.quantity) <= (parseFloat(m.reorder_level) || 100)).length;

        return {
            head_office_stock,
            manufacturing_stock: 0, // This will come from manufacturing_stock table
            total_value,
            low_stock_items
        };
    };

    // Calculate finished goods stock summary
    const calculateFinishedGoodsStock = (goods) => {
        const manufacturing_stock = goods.reduce((sum, g) => sum + (parseFloat(g.manufacturing_stock) || 0), 0);
        const warehouse_stock = goods.reduce((sum, g) => sum + (parseFloat(g.warehouse_stock) || 0), 0);
        const total_value = goods.reduce((sum, g) => sum + ((parseFloat(g.manufacturing_stock) || 0) * (parseFloat(g.cost_price) || 0)), 0);
        const low_stock_items = goods.filter(g => parseFloat(g.manufacturing_stock) <= (parseFloat(g.alert_quantity) || 500)).length;

        return {
            manufacturing_stock,
            warehouse_stock,
            total_value,
            low_stock_items
        };
    };

    // Calculate waste by type
    const calculateWasteByType = (summary) => {
        return {
            raw_material: summary.total_wastage_cost * 0.6, // Example - should come from actual data
            finished_good: summary.total_wastage_cost * 0.4
        };
    };

    // Handle refresh
    const handleRefresh = async () => {
        setRefreshing(true);
        await loadDashboardData();
        setLastUpdated(new Date());
        toast.success('Dashboard refreshed');
    };

    // Handle date range change
    const handleDateRangeChange = (range) => {
        const today = new Date();
        let from_date = '';
        let to_date = today.toISOString().split('T')[0];

        switch (range) {
            case 'today':
                from_date = to_date;
                break;
            case 'week':
                from_date = new Date(today.setDate(today.getDate() - 7)).toISOString().split('T')[0];
                break;
            case 'month':
                from_date = new Date(today.setMonth(today.getMonth() - 1)).toISOString().split('T')[0];
                break;
            case 'quarter':
                from_date = new Date(today.setMonth(today.getMonth() - 3)).toISOString().split('T')[0];
                break;
            case 'year':
                from_date = new Date(today.setFullYear(today.getFullYear() - 1)).toISOString().split('T')[0];
                break;
            default:
                from_date = new Date(today.setMonth(today.getMonth() - 1)).toISOString().split('T')[0];
        }

        setFilters({ ...filters, date_range: range, from_date, to_date });
    };

    // Load data on mount and when filters change
    useEffect(() => {
        loadDashboardData();
    }, [loadDashboardData]);

    // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount || 0);
    };

    // Format number
    const formatNumber = (num) => {
        const parsed = parseFloat(num);
        return new Intl.NumberFormat('en-IN').format(isNaN(parsed) ? 0 : parsed);
    };

    // Format date
    const formatDate = (date) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    // Get status badge
    const getStatusBadge = (status) => {
        const map = {
            pending: 'bg-warning',
            dispatched: 'bg-primary',
            received: 'bg-success',
            completed: 'bg-success',
            cancelled: 'bg-danger',
            draft: 'bg-secondary'
        };
        return <span className={`badge ${map[status] || 'bg-secondary'}`}>{status?.toUpperCase() || 'N/A'}</span>;
    };

    // Render tooltip
    const renderTooltip = (props, text) => (
        <Tooltip id={`tooltip-${text}`} {...props}>{text}</Tooltip>
    );

    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Header */}
                <div className="page-header">
                    <div className="page-title">
                        <h4>Manufacturing Dashboard</h4>
                        <h6>Real-time overview of manufacturing operations</h6>
                    </div>

                    <ul className="table-top-head">
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Export PDF')}>
                                <Link to="#">
                                    <ImageWithBasePath src="assets/img/icons/pdf.svg" alt="PDF" />
                                </Link>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Export Excel')}>
                                <Link to="#">
                                    <ImageWithBasePath src="assets/img/icons/excel.svg" alt="Excel" />
                                </Link>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Print')}>
                                <Link onClick={() => window.print()}>
                                    <ImageWithBasePath src="assets/img/icons/printer.svg" alt="Print" />
                                </Link>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Refresh')}>
                                <Link onClick={handleRefresh} className={refreshing ? 'spinner' : ''}>
                                    <RefreshCw size={18} />
                                </Link>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Collapse')}>
                                <Link
                                    className={data ? "active" : ""}
                                    onClick={() => dispatch(setToogleHeader(!data))}
                                >
                                    <ChevronRight size={18} />
                                </Link>
                            </OverlayTrigger>
                        </li>
                    </ul>
                </div>

                {/* Date Range Filter */}
                <div className="card mb-4">
                    <div className="card-body">
                        <div className="row align-items-center">
                            <div className="col-lg-8">
                                <div className="btn-group" role="group">
                                    <button
                                        className={`btn btn-outline-secondary ${filters.date_range === 'today' ? 'active' : ''}`}
                                        onClick={() => handleDateRangeChange('today')}
                                    >
                                        Today
                                    </button>
                                    <button
                                        className={`btn btn-outline-secondary ${filters.date_range === 'week' ? 'active' : ''}`}
                                        onClick={() => handleDateRangeChange('week')}
                                    >
                                        This Week
                                    </button>
                                    <button
                                        className={`btn btn-outline-secondary ${filters.date_range === 'month' ? 'active' : ''}`}
                                        onClick={() => handleDateRangeChange('month')}
                                    >
                                        This Month
                                    </button>
                                    <button
                                        className={`btn btn-outline-secondary ${filters.date_range === 'quarter' ? 'active' : ''}`}
                                        onClick={() => handleDateRangeChange('quarter')}
                                    >
                                        This Quarter
                                    </button>
                                    <button
                                        className={`btn btn-outline-secondary ${filters.date_range === 'year' ? 'active' : ''}`}
                                        onClick={() => handleDateRangeChange('year')}
                                    >
                                        This Year
                                    </button>
                                </div>
                            </div>
                            <div className="col-lg-4 text-end">
                                <small className="text-muted">
                                    <Calendar size={14} className="me-1" />
                                    Last updated: {lastUpdated.toLocaleTimeString()}
                                </small>
                            </div>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-2 text-muted">Loading manufacturing data...</p>
                    </div>
                ) : (
                    <>
                        {/* KPI Cards */}
                        <div className="row">
                            <div className="col-lg-3 col-md-6 col-sm-12">
                                <div className="card">
                                    <div className="card-body">
                                        <div className="d-flex align-items-center justify-content-between">
                                            <div>
                                                <p className="text-muted mb-1">Total Raw Materials</p>
                                                <h3 className="mb-0">{formatNumber(dashboardData.summary.total_raw_materials)}</h3>
                                                <small className="text-success">
                                                    <Package size={12} className="me-1" />
                                                    {formatNumber(dashboardData.stock_summary.raw_materials.head_office_stock)} units in stock
                                                </small>
                                            </div>
                                            <div className="bg-primary bg-opacity-10 p-3 rounded">
                                                <Database size={24} className="text-primary" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-lg-3 col-md-6 col-sm-12">
                                <div className="card">
                                    <div className="card-body">
                                        <div className="d-flex align-items-center justify-content-between">
                                            <div>
                                                <p className="text-muted mb-1">Finished Goods</p>
                                                <h3 className="mb-0">{formatNumber(dashboardData.summary.total_finished_goods)}</h3>
                                                <small className="text-info">
                                                    <Package size={12} className="me-1" />
                                                    {formatNumber(dashboardData.stock_summary.finished_goods.manufacturing_stock)} units in mfg
                                                </small>
                                            </div>
                                            <div className="bg-success bg-opacity-10 p-3 rounded">
                                                <Package size={24} className="text-success" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-lg-3 col-md-6 col-sm-12">
                                <div className="card">
                                    <div className="card-body">
                                        <div className="d-flex align-items-center justify-content-between">
                                            <div>
                                                <p className="text-muted mb-1">Production Value</p>
                                                <h3 className="mb-0">{formatCurrency(dashboardData.summary.total_production_value)}</h3>
                                                <small className="text-warning">
                                                    <TrendingUp size={12} className="me-1" />
                                                    {dashboardData.summary.total_productions} productions
                                                </small>
                                            </div>
                                            <div className="bg-warning bg-opacity-10 p-3 rounded">
                                                <DollarSign size={24} className="text-warning" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-lg-3 col-md-6 col-sm-12">
                                <div className="card">
                                    <div className="card-body">
                                        <div className="d-flex align-items-center justify-content-between">
                                            <div>
                                                <p className="text-muted mb-1">Conversion Rate</p>
                                                <h3 className="mb-0">{dashboardData.summary.conversion_rate}%</h3>
                                                <small className="text-danger">
                                                    <AlertTriangle size={12} className="me-1" />
                                                    Waste: {formatCurrency(dashboardData.summary.total_waste_cost)}
                                                </small>
                                            </div>
                                            <div className="bg-danger bg-opacity-10 p-3 rounded">
                                                <TrendingUp size={24} className="text-danger" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Stock Summary Cards */}
                        {/* <div className="row">
                            <div className="col-lg-6">
                                <div className="card">
                                    <div className="card-header">
                                        <h5 className="mb-0">Raw Material Stock Summary</h5>
                                    </div>
                                    <div className="card-body">
                                        <div className="row">
                                            <div className="col-md-6 text-center">
                                                <h6>Head Office Stock</h6>
                                                <h3 className="text-primary">
                                                    {formatNumber(dashboardData.stock_summary.raw_materials.head_office_stock)}
                                                </h3>
                                                <small>units</small>
                                            </div>
                                            <div className="col-md-6 text-center">
                                                <h6>Manufacturing Stock</h6>
                                                <h3 className="text-info">
                                                    {formatNumber(dashboardData.stock_summary.raw_materials.manufacturing_stock)}
                                                </h3>
                                                <small>units</small>
                                            </div>
                                        </div>
                                        <hr />
                                        <div className="d-flex justify-content-between">
                                            <span>Total Value:</span>
                                            <strong>{formatCurrency(dashboardData.stock_summary.raw_materials.total_value)}</strong>
                                        </div>
                                        <div className="d-flex justify-content-between mt-2">
                                            <span>Low Stock Items:</span>
                                            <span className={dashboardData.stock_summary.raw_materials.low_stock_items > 0 ? 'text-danger' : 'text-success'}>
                                                {dashboardData.stock_summary.raw_materials.low_stock_items}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-lg-6">
                                <div className="card">
                                    <div className="card-header">
                                        <h5 className="mb-0">Finished Goods Stock Summary</h5>
                                    </div>
                                    <div className="card-body">
                                        <div className="row">
                                            <div className="col-md-6 text-center">
                                                <h6>Manufacturing Stock</h6>
                                                <h3 className="text-warning">
                                                    {formatNumber(dashboardData.stock_summary.finished_goods.manufacturing_stock)}
                                                </h3>
                                                <small>units</small>
                                            </div>
                                            <div className="col-md-6 text-center">
                                                <h6>Warehouse Stock</h6>
                                                <h3 className="text-success">
                                                    {formatNumber(dashboardData.stock_summary.finished_goods.warehouse_stock)}
                                                </h3>
                                                <small>units</small>
                                            </div>
                                        </div>
                                        <hr />
                                        <div className="d-flex justify-content-between">
                                            <span>Total Value:</span>
                                            <strong>{formatCurrency(dashboardData.stock_summary.finished_goods.total_value)}</strong>
                                        </div>
                                        <div className="d-flex justify-content-between mt-2">
                                            <span>Low Stock Items:</span>
                                            <span className={dashboardData.stock_summary.finished_goods.low_stock_items > 0 ? 'text-danger' : 'text-success'}>
                                                {dashboardData.stock_summary.finished_goods.low_stock_items}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div> */}

                        {/* Pending Actions Alert */}
                        {/* {(dashboardData.pending_actions.pending_dispatches > 0 ||
                            dashboardData.pending_actions.pending_receives > 0 ||
                            dashboardData.pending_actions.pending_transfers > 0 ||
                            dashboardData.pending_actions.productions_to_convert > 0) && (
                                <div className="alert alert-warning">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <AlertTriangle size={18} className="me-2" />
                                            <strong>Pending Actions:</strong>
                                            {dashboardData.pending_actions.pending_dispatches > 0 && (
                                                <span className="badge bg-warning ms-2">{dashboardData.pending_actions.pending_dispatches} Dispatches</span>
                                            )}
                                            {dashboardData.pending_actions.pending_receives > 0 && (
                                                <span className="badge bg-info ms-2">{dashboardData.pending_actions.pending_receives} Receives</span>
                                            )}
                                            {dashboardData.pending_actions.pending_transfers > 0 && (
                                                <span className="badge bg-secondary ms-2">{dashboardData.pending_actions.pending_transfers} Transfers</span>
                                            )}
                                            {dashboardData.pending_actions.productions_to_convert > 0 && (
                                                <span className="badge bg-success ms-2">{dashboardData.pending_actions.productions_to_convert} Ready to Convert</span>
                                            )}
                                        </div>
                                        <Link to="/manufacturing/dispatch" className="btn btn-sm btn-warning">
                                            View All <ArrowRight size={14} />
                                        </Link>
                                    </div>
                                </div>
                            )} */}

                        {/* Alerts Section */}
                        {(dashboardData.alerts.low_stock_raw.length > 0 || dashboardData.alerts.low_stock_finished.length > 0) && (
                            <div className="card mb-4 border-danger">
                                <div className="card-header bg-danger bg-opacity-10">
                                    <h5 className="mb-0 text-danger">
                                        <AlertTriangle size={18} className="me-2" />
                                        Stock Alerts
                                    </h5>
                                </div>
                                <div className="card-body">
                                    <div className="row">
                                        {dashboardData.alerts.low_stock_raw.length > 0 && (
                                            <div className="col-md-6">
                                                <h6>Raw Materials Low Stock:</h6>
                                                <ul className="list-unstyled">
                                                    {dashboardData.alerts.low_stock_raw.slice(0, 5).map((item, idx) => (
                                                        <li key={idx} className="mb-2">
                                                            <span className="text-danger">⚠️</span> {item.material_name}
                                                            <span className="float-end">Stock: {item.quantity} / Reorder: {item.reorder_level}</span>
                                                        </li>
                                                    ))}
                                                    {dashboardData.alerts.low_stock_raw.length > 5 && (
                                                        <li className="text-muted">+{dashboardData.alerts.low_stock_raw.length - 5} more</li>
                                                    )}
                                                </ul>
                                            </div>
                                        )}
                                        {dashboardData.alerts.low_stock_finished.length > 0 && (
                                            <div className="col-md-6">
                                                <h6>Finished Goods Low Stock:</h6>
                                                <ul className="list-unstyled">
                                                    {dashboardData.alerts.low_stock_finished.slice(0, 5).map((item, idx) => (
                                                        <li key={idx} className="mb-2">
                                                            <span className="text-warning">⚠️</span> {item.product_name}
                                                            <span className="float-end">Stock: {item.manufacturing_stock} / Alert: {item.alert_quantity}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Recent Activities */}
                        <div className="row">
                            {/* <div className="col-lg-4">
                                <div className="card">
                                    <div className="card-header">
                                        <h5 className="mb-0">
                                            <Truck size={16} className="me-2" />
                                            Recent Dispatches
                                        </h5>
                                    </div>
                                    <div className="card-body p-0">
                                        {dashboardData.recent_activities.dispatches.length === 0 ? (
                                            <div className="text-center py-4 text-muted">
                                                <Package size={32} />
                                                <p className="mt-2">No recent dispatches</p>
                                            </div>
                                        ) : (
                                            <div className="list-group list-group-flush">
                                                {dashboardData.recent_activities.dispatches.map((dispatch, idx) => (
                                                    <div key={idx} className="list-group-item">
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <div>
                                                                <strong>{dispatch.dispatch_no}</strong>
                                                                <div className="small text-muted">
                                                                    From: {dispatch.from_location} → To: {dispatch.to_location}
                                                                </div>
                                                                <div className="small">
                                                                    {dispatch.total_items} items, {dispatch.total_quantity} units
                                                                </div>
                                                            </div>
                                                            <div>
                                                                {getStatusBadge(dispatch.status)}
                                                                <div className="small text-muted mt-1">
                                                                    {formatDate(dispatch.dispatch_date)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <div className="card-footer">
                                        <Link to="/manufacturing/dispatch" className="btn btn-sm btn-outline-primary w-100">
                                            View All Dispatches <ArrowRight size={14} />
                                        </Link>
                                    </div>
                                </div>
                            </div>

                            <div className="col-lg-4">
                                <div className="card">
                                    <div className="card-header">
                                        <h5 className="mb-0">
                                            <Download size={16} className="me-2" />
                                            Recent Receives
                                        </h5>
                                    </div>
                                    <div className="card-body p-0">
                                        {dashboardData.recent_activities.receives.length === 0 ? (
                                            <div className="text-center py-4 text-muted">
                                                <Download size={32} />
                                                <p className="mt-2">No recent receives</p>
                                            </div>
                                        ) : (
                                            <div className="list-group list-group-flush">
                                                {dashboardData.recent_activities.receives.map((receive, idx) => (
                                                    <div key={idx} className="list-group-item">
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <div>
                                                                <strong>{receive.receive_no}</strong>
                                                                <div className="small text-muted">
                                                                    Dispatch: {receive.dispatch_no}
                                                                </div>
                                                                <div className="small">
                                                                    Received: {receive.total_received || 0} / Damage: {receive.total_damage || 0}
                                                                </div>
                                                            </div>
                                                            <div>
                                                                {getStatusBadge(receive.status)}
                                                                <div className="small text-muted mt-1">
                                                                    {formatDate(receive.receive_date)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <div className="card-footer">
                                        <Link to="/manufacturing/receive" className="btn btn-sm btn-outline-primary w-100">
                                            View All Receives <ArrowRight size={14} />
                                        </Link>
                                    </div>
                                </div>
                            </div> */}

                            <div className="col-lg-12">
                                <div className="card">
                                    <div className="card-header">
                                        <h5 className="mb-0">
                                            <Settings size={16} className="me-2" />
                                            Recent Productions
                                        </h5>
                                    </div>
                                    <div className="card-body p-0">
                                        {dashboardData.recent_activities.productions.length === 0 ? (
                                            <div className="text-center py-4 text-muted">
                                                <Settings size={32} />
                                                <p className="mt-2">No recent productions</p>
                                            </div>
                                        ) : (
                                            <div className="list-group list-group-flush">
                                                {dashboardData.recent_activities.productions.map((production, idx) => (
                                                    <div key={idx} className="list-group-item">
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <div>
                                                                <strong>{production.production_no}</strong>
                                                                <div className="small text-muted">
                                                                    Batch: {production.batch_no}
                                                                </div>
                                                                <div className="small">
                                                                    {production.finished_goods_count || 0} items, {production.total_quantity || 0} units
                                                                </div>
                                                            </div>
                                                            <div>
                                                                {getStatusBadge(production.status)}
                                                                <div className="small text-muted mt-1">
                                                                    {formatDate(production.production_date)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <div className="card-footer">
                                        <Link to="/manufacturing/history" className="btn btn-sm btn-outline-primary w-100">
                                            View All Productions <ArrowRight size={14} />
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Monthly Production Trend */}
                        {dashboardData.charts.monthly_production.length > 0 && (
                            <div className="card mt-4">
                                <div className="card-header">
                                    <h5 className="mb-0">
                                        <BarChart2 size={16} className="me-2" />
                                        Monthly Production Trend
                                    </h5>
                                </div>
                                <div className="card-body">
                                    <div className="table-responsive">
                                        <table className="table table-sm">
                                            <thead>
                                                <tr>
                                                    <th>Month</th>
                                                    <th>Productions</th>
                                                    <th>Raw Material Cost</th>
                                                    <th>Production Cost</th>
                                                    <th>Waste Cost</th>
                                                    <th>Efficiency</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {dashboardData.charts.monthly_production.map((month, idx) => {
                                                    const efficiency = month.production_cost > 0
                                                        ? ((month.production_cost - month.waste_cost) / month.production_cost * 100).toFixed(1)
                                                        : 0;
                                                    return (
                                                        <tr key={idx}>
                                                            <td>{month.month}</td>
                                                            <td>{month.productions}</td>
                                                            <td>{formatCurrency(month.raw_cost)}</td>
                                                            <td>{formatCurrency(month.production_cost)}</td>
                                                            <td className="text-danger">{formatCurrency(month.waste_cost)}</td>
                                                            <td>
                                                                <span className={efficiency > 90 ? 'text-success' : efficiency > 80 ? 'text-warning' : 'text-danger'}>
                                                                    {efficiency}%
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Quick Actions */}
                        <div className="row mt-4">
                            <div className="col-12">
                                <div className="card">
                                    <div className="card-header">
                                        <h5 className="mb-0">Quick Actions</h5>
                                    </div>
                                    <div className="card-body">
                                        <div className="row g-3">
                                            <div className="col-md-3">
                                                <Link to="/manufacturing/raw-material-purchase" className="btn btn-outline-primary w-100 py-3">
                                                    <ShoppingBag size={20} className="mb-2 d-block mx-auto" />
                                                    Purchase Raw Material
                                                </Link>
                                            </div>
                                            {/* <div className="col-md-3">
                                                <Link to="/manufacturing/dispatch" className="btn btn-outline-primary w-100 py-3">
                                                    <Truck size={20} className="mb-2 d-block mx-auto" />
                                                    Dispatch to Mfg
                                                </Link>
                                            </div> */}
                                            <div className="col-md-3">
                                                <Link to="/manufacturing/production" className="btn btn-outline-primary w-100 py-3">
                                                    <Settings size={20} className="mb-2 d-block mx-auto" />
                                                    Create Production
                                                </Link>
                                            </div>
                                            {/* <div className="col-md-3">
                                                <Link to="/manufacturing/convert" className="btn btn-outline-success w-100 py-3">
                                                    <ShoppingBag size={20} className="mb-2 d-block mx-auto" />
                                                    Convert to Purchase
                                                </Link>
                                            </div> */}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ManufacturingDashboard;